import { Navigate, Outlet, useSearchParams } from 'react-router';

import { useRequestGatewayContext } from '../context/useRequestGatewayContext';
import { RequestGatewayContext } from '../context/RequestGatewayContext';
import { useTokensStore } from '../stores/useTokensStore';

/**
 * Props for a protected route component.
 */
export type ProtectedRouteProps = {
  /**
   * The path to redirect to if the user is not authenticated.
   */
  redirectPath: string;
  /**
   * Whether to keep original url in order to redirect back. If true, the original url saved
   * as query param for redirect target url
   * @default false
   */
  storeOriginalUrl?: boolean;
  baseUrl?: string;

  /**
   * Whether to invert the redirect logic. If true, the user will be redirected
   * if they are authenticated.
   * @default false
   */
  invertRedirectLogic?: boolean;
};

export const ProtectedRoute = ({
  redirectPath,
  children,
  baseUrl: childBaseUrl,
  invertRedirectLogic = false,
  storeOriginalUrl = false,
}: React.PropsWithChildren<ProtectedRouteProps>) => {
  const [searchParams] = useSearchParams();

  const { baseUrl, refreshTokenUrl, tokenStorage, version, source } =
    useRequestGatewayContext();
  const accessToken = useTokensStore((store) => store.accessToken);

  const shouldRedirect = invertRedirectLogic ? !!accessToken : !accessToken;

  if (shouldRedirect) {
    const originalUrl = searchParams.get('originalUrl');

    if (storeOriginalUrl) {
      const { pathname: currentPathname, search: currentSearch } =
        window.location;

      return (
        <Navigate
          to={`${redirectPath}?originalUrl=${encodeURIComponent(
            currentPathname + currentSearch
          )}`}
          replace
        />
      );
    }

    return <Navigate to={originalUrl || redirectPath} replace />;
  }

  return (
    <RequestGatewayContext.Provider
      value={{
        isProtectedRoute: !invertRedirectLogic,
        baseUrl: childBaseUrl || baseUrl,
        version,
        source,
        tokenStorage,
        refreshTokenUrl,
      }}
    >
      {children ? children : <Outlet />}
    </RequestGatewayContext.Provider>
  );
};
